import React, { FC, memo, useContext, useState } from 'react'

import { pick } from 'lodash-es'
import { FormProvider, useForm } from 'react-hook-form'
import { useWizardStep } from 'react-wizard-primitive'

import { AppContext } from 'Const'
import { useCustomer, useSignUpState } from 'Hooks/checkout'
import { useCodeStepFocus, useCustomerContactMutations, useResendCooldown } from 'Hooks/checkoutContact'
import useEvent from 'Hooks/useEvent'

import { StepContext } from '..'
import NextStepBtn from '../NextStepBtn'

import ContactStepHeader from './ContactStepHeader'
import Details from './Details'
import LoggedCustomerView from './LoggedCustomerView'
import Password from './Password'
import PhoneVerification, { getPhoneSectionDescription } from './PhoneVerification'
import './index.scss'

export interface FormState {
  code?: string
  email: string
  firstName: string
  lastName: string
  password?: string
  phone: string
  subscribed?: boolean
}

const DEFAULT_FORM_VALUES: FormState = {
  code: '',
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  phone: '',
  subscribed: true,
}

const OTP_LENGTH = 6

const CustomerFieldsGroup: FC = () => {
  const { shop } = useContext(AppContext)
  const requiresSmsOtp = shop?.twoFactorAuth === true
  const { checkouting, nextStep } = useContext(StepContext)
  const { isActive, resetToStep } = useWizardStep()
  const customer = useCustomer()
  const [state, setState] = useSignUpState()
  const form = useForm<FormState>({
    defaultValues: DEFAULT_FORM_VALUES,
  })
  const { resendSecondsLeft, startResendCooldown, resetCooldown } = useResendCooldown()
  const [statusMessage, setStatusMessage] = useState('')

  const { clearErrors, handleSubmit, reset, getValues, setError, setValue } = form

  const { codeInputRef, hasVisitedCodeStepRef, resetCodeStepTracking } = useCodeStepFocus({
    requiresSmsOtp,
    setStatusMessage,
    startResendCooldown,
    stateIsCode: state.isCode,
  })

  const onLogoutClearClient = useEvent(() => {
    resetCooldown()
    setStatusMessage('')
    resetCodeStepTracking()
    reset(DEFAULT_FORM_VALUES)
  })

  const { handleLogout, signUp, signUpLoading } = useCustomerContactMutations({
    nextStep,
    onLogoutClearClient,
    requiresSmsOtp,
    resetToStep,
    setState,
  })

  const onSubmit = handleSubmit(
    (input: FormState) => {
      if (requiresSmsOtp && state.isCode && (input.code || '').trim().length !== OTP_LENGTH) {
        setStatusMessage('Enter the 6-digit verification code to continue checkout.')
        setError('code', { message: 'Enter the 6-digit code', type: 'manual' })
        codeInputRef.current?.focus()
        return
      }

      if (customer?.signedUp) {
        nextStep({ customer: pick(customer, ['firstName', 'lastName', 'email', 'phone']) })
        return
      }
      void signUp({ variables: { input } })
    },
    (errors) => {
      if (requiresSmsOtp && errors.code) {
        setStatusMessage('Enter the 6-digit verification code to continue checkout.')
        codeInputRef.current?.focus()
      }
    },
  )

  const handleResend = useEvent(() => {
    if (resendSecondsLeft > 0) return
    if (requiresSmsOtp) {
      startResendCooldown()
      setStatusMessage('Verification code resent. Enter the 6-digit code from SMS.')
    }
    clearErrors('code')
    setValue('code', '')
    codeInputRef.current?.focus()
    void signUp({ variables: { input: { phone: getValues('phone') } } })
  })

  const activeSection = state.isCode ? 'code' : state.isForm ? 'details' : state.isPassword ? 'password' : 'phone'
  const phoneStepComplete = state.isForm || state.isPassword || !!customer?.signedUp
  const showSmsVerifiedBadge = requiresSmsOtp && hasVisitedCodeStepRef.current && phoneStepComplete
  const phonePanelTitle = requiresSmsOtp ? 'Verify Phone' : 'Phone number'
  const phoneSectionDescription = getPhoneSectionDescription({
    requiresSmsOtp,
    isPhoneStepComplete: phoneStepComplete,
    isOnCodeStep: state.isCode,
    hasVisitedCodeStep: hasVisitedCodeStepRef.current,
  })

  let activeSectionTitle: string | null = null
  let activeSectionDescription: string | null = null

  if (state.isForm) {
    activeSectionTitle = 'Contact Details'
    activeSectionDescription = 'Add your contact details to continue checkout'
  } else if (state.isPassword) {
    activeSectionTitle = 'Welcome Back'
    activeSectionDescription = 'Sign in to continue with your saved customer profile'
  }

  const headerSecondaryAction = !isActive ? 'change' : state.isPhone && !customer?.signedUp ? null : 'logout'

  return (
    <FormProvider {...form}>
      <div className="checkout-step / mb-5">
        <ContactStepHeader secondaryAction={headerSecondaryAction} onChangeStep={resetToStep} onLogout={handleLogout} />

        <form className="checkout-contact-form" hidden={!isActive || customer?.signedUp} onSubmit={onSubmit}>
          <p aria-live="polite" className="sr-only" role="status">
            {statusMessage}
          </p>

          <div className="checkout-contact-flow">
            {customer?.signedUp ? null : (
              <PhoneVerification
                canAutoSubmitCode={!(signUpLoading || checkouting)}
                checkouting={checkouting}
                codeInputRef={codeInputRef}
                handleResend={handleResend}
                phonePanelTitle={phonePanelTitle}
                phoneSectionDescription={phoneSectionDescription}
                resendSecondsLeft={resendSecondsLeft}
                showSmsVerifiedBadge={showSmsVerifiedBadge}
                signUpLoading={signUpLoading}
                stateIsCode={state.isCode}
                stateIsPhone={state.isPhone}
                onSubmit={onSubmit}
              />
            )}

            {state.isForm || state.isPassword ? (
              <section
                key={activeSection}
                className="checkout-contact-flow__panel checkout-contact-flow__panel--active"
                data-step={activeSection}
              >
                {activeSectionTitle ? <h3 className="checkout-contact-flow__title">{activeSectionTitle}</h3> : null}
                {activeSectionDescription ? (
                  <p className="checkout-contact-flow__description mb-2">{activeSectionDescription}</p>
                ) : null}

                {state.isForm ? <Details /> : state.isPassword ? <Password /> : null}
              </section>
            ) : null}
          </div>
        </form>
        {customer?.signedUp ? (
          <LoggedCustomerView email={customer.email} firstName={customer.firstName} lastName={customer.lastName} />
        ) : null}
      </div>

      {isActive ? <NextStepBtn loading={signUpLoading || checkouting} onSubmit={onSubmit} /> : null}
    </FormProvider>
  )
}

export default memo(CustomerFieldsGroup)
